import { AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { FieldState, ObjectState } from "src/utils/formState";
import { AiFieldProposalFormValue } from "./aiFieldProposalForm";
import { AiProposalParse } from "./bindAiProposal";

/**
 * One entity field plus how to coerce its proposal string into that field's type.
 * `T` is erased in mixed arrays (`AiFieldBinding[]`); use `aiFieldBinding` so each entry keeps field and parse aligned.
 */
export type AiFieldBinding<T = any> = {
  field: FieldState<T>;
  parse: AiProposalParse<T>;
  proposal?: ObjectState<AiFieldProposalFormValue>;
};

export function aiFieldBinding<T>(
  field: FieldState<T>,
  parse: AiProposalParse<NoInfer<T>>,
  proposal?: ObjectState<AiFieldProposalFormValue>,
): AiFieldBinding<T> {
  return { field, parse, proposal };
}

/**
 * Accept All / Ignore All for the bindings you pass. Does not call a mutation.
 *
 * Accepted: parse `fieldValue`, `field.set` it, mark Accepted.
 * Rejected: mark Rejected only.
 *
 * **Every pending proposal it is given ends up resolved**, which matters because the alternative
 * strands a row. A value we can't parse — an empty `fieldValue`, or a room number outside its level's
 * range — can't be applied, so it is recorded as Rejected: we couldn't honor it. Leaving it Pending
 * instead means the review banner never clears and the user has no way to dismiss it but editing that
 * one field.
 */
export function reconcilePendingAiProposals(
  bindings: AiFieldBinding[],
  decision: AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected,
  /**
   * Proposals with no editable field behind them — a delete's `active`, say. These state a whole-row
   * intent rather than a value, so they take `decision` as given. Pass the entity's full set; the
   * field-backed ones are recognised from `bindings`.
   */
  fieldlessProposals: ObjectState<AiFieldProposalFormValue>[] = [],
): void {
  const fieldBacked = new Set(bindings.flatMap(({ proposal }) => (proposal ? [proposal] : [])));
  for (const { field, parse, proposal } of bindings) {
    // No proposal on this field, or already Accepted/Rejected.
    if (proposal?.status.value !== AiFieldProposalStatus.Pending) continue;
    if (decision === AiFieldProposalStatus.Accepted) {
      // Coerce GraphQL's string `fieldValue` into the entity field's type.
      const proposedValue = parse(proposal.fieldValue.value);
      // Unparseable (empty or junk) — not "clear the field". Leave the value be; the sweep below
      // still resolves the proposal so the row doesn't get stuck.
      if (proposedValue === undefined) continue;
      field.set(proposedValue);
    }
    proposal.status.set(decision);
  }
  for (const proposal of new Set([...fieldBacked, ...fieldlessProposals])) {
    if (proposal.status.value !== AiFieldProposalStatus.Pending) continue;
    proposal.status.set(fieldBacked.has(proposal) ? AiFieldProposalStatus.Rejected : decision);
  }
}
